import {
  CardRecord,
  CardType,
  CardMode,
  MyIELTSDatabase,
  TODAY_SNAPSHOT_ID,
  TodaySnapshot,
  WritingContent,
  WritingMeta,
  SrsPreferences,
  defaultDb,
  getDueCards,
  getSettings,
} from '../db/indexedDb';
import {
  randomizeSpeakingTopics,
  randomizeSimpleTopics,
  randomizeAIWritingTopics,
  getDailySeed,
} from './topicSorter';
import { DEFAULT_SPEAKING_PART_RATIO } from '../config/srsDefaults';
import { getWritingMode, type WritingModeType } from '../config/writingMode';
import { isPublicDeploy } from '../config/features';

export const CARD_TYPE_ORDER: CardType[] = ['speaking', 'writing', 'listening', 'vocab'];

export const DEFAULT_DAILY_LIMITS: Record<CardType, number> = {
  speaking: 6,
  writing: 10,
  listening: 10,
  vocab: 15,
};

export type DailyLimits = Partial<Record<CardType, number>>;

export interface TodaySkillPlan {
  type: CardType;
  dueCount: number;
  plannedCount: number;
  overflowCount: number;
  limit: number;
  plannedCards: CardRecord[];
}

export interface TodayPlan {
  skills: TodaySkillPlan[];
  totals: {
    dueCount: number;
    plannedCount: number;
    overflowCount: number;
  };
}

export interface TodayPlanResult {
  plan: TodayPlan;
  limits: Record<CardType, number>;
  rawDue: Partial<Record<CardType, CardRecord[]>>;
  cutoff: number;
}

export interface TodayPlanOptions {
  writingModeOverride?: WritingModeType;
}

export interface MixedQueueOptions {
  skillWeights?: Partial<Record<CardType, number>>;
}

const defaultWeights: Record<CardType, number> = {
  speaking: 1,
  writing: 1,
  listening: 1,
  vocab: 1,
};

export function buildMixedQueue(plan: TodayPlan, opts: MixedQueueOptions = {}): CardRecord[] {
  const weights = { ...defaultWeights, ...(opts.skillWeights ?? {}) };
  const all = plan.skills.flatMap((skill) => skill.plannedCards.map((card) => ({ card, skill: skill.type })));

  return all
    .sort((a, b) => {
      if (a.card.mode !== b.card.mode) return (modeRank[a.card.mode] ?? 99) - (modeRank[b.card.mode] ?? 99);
      if ((b.card.priority ?? 0) !== (a.card.priority ?? 0)) return (b.card.priority ?? 0) - (a.card.priority ?? 0);
      const overdueA = a.card.nextReviewDate;
      const overdueB = b.card.nextReviewDate;
      if (overdueA !== overdueB) return overdueA - overdueB;
      const weightDiff = (weights[b.skill] ?? 1) - (weights[a.skill] ?? 1);
      if (weightDiff !== 0) return weightDiff;
      return a.card.id.localeCompare(b.card.id);
    })
    .map((item) => item.card);
}

const clampLimit = (value: number | undefined) => Math.max(0, value ?? 0);

export const endOfDay = (timestamp: number) => {
  const dt = new Date(timestamp);
  dt.setHours(23, 59, 59, 999);
  return dt.getTime();
};

export function mergeLimits(overrides?: DailyLimits): Record<CardType, number> {
  return CARD_TYPE_ORDER.reduce<Record<CardType, number>>((acc, type) => {
    const override = overrides?.[type];
    acc[type] = clampLimit(override ?? DEFAULT_DAILY_LIMITS[type]);
    return acc;
  }, {} as Record<CardType, number>);
}

const modeRank: Partial<Record<CardMode, number>> = { review: 0, relearn: 1, 'topic-recall': 2, 'topic-dictation': 3 };

// 静态写作卡片排序（保持原有逻辑）
const sortStaticWritingCards = (cards: CardRecord[]) => {
  // 分离不同类型的卡片
  const topicDictationCards = cards.filter(c => c.mode === 'topic-dictation');
  const topicRecallCards = cards.filter(c => c.mode === 'topic-recall');
  const sentenceCards = cards.filter(c => c.mode !== 'topic-recall' && c.mode !== 'topic-dictation');

  // 按话题分组句子卡片
  const topicGroups = new Map<string, CardRecord[]>();
  for (const card of sentenceCards) {
    const writingMeta = card.writingMeta as WritingMeta | undefined;
    const writingContent = card.content as WritingContent | undefined;
    const topic = writingMeta?.topicTag ?? writingContent?.topicTag ?? 'unknown';
    if (!topicGroups.has(topic)) topicGroups.set(topic, []);
    topicGroups.get(topic)!.push(card);
  }

  // 每组内部排序
  const sortGroup = (group: CardRecord[]) => group.sort((a, b) => {
    if (a.mode !== b.mode) return (modeRank[a.mode] ?? 99) - (modeRank[b.mode] ?? 99);
    if ((b.priority ?? 0) !== (a.priority ?? 0)) return (b.priority ?? 0) - (a.priority ?? 0);
    if (a.nextReviewDate !== b.nextReviewDate) return a.nextReviewDate - b.nextReviewDate;
    return a.id.localeCompare(b.id);
  });

  // 构建结果：每个话题的句子卡片后面跟着话题回忆卡片
  const result: CardRecord[] = [];
  for (const [topic, group] of topicGroups.entries()) {
    result.push(...sortGroup(group));
    // 找到该话题的回忆卡片并插入
    const recallCard = topicRecallCards.find(c => {
      const meta = c.writingMeta as WritingMeta | undefined;
      const content = c.content as WritingContent | undefined;
      return meta?.topicTag === topic || content?.topicTag === topic;
    });
    if (recallCard) result.push(recallCard);
  }

  // 话题默写卡片按 dueDate 排序后附加到最后
  const sortedDictationCards = topicDictationCards.sort((a, b) => {
    if (a.nextReviewDate !== b.nextReviewDate) return a.nextReviewDate - b.nextReviewDate;
    return a.id.localeCompare(b.id);
  });
  result.push(...sortedDictationCards);

  return result;
};

// 主题随机排序（根据卡片类型应用不同策略）
const sortCardsForPlanning = (
  cards: CardRecord[],
  type: CardType,
  srsPrefs?: SrsPreferences,
  options?: TodayPlanOptions
) => {
  if (cards.length === 0) return [];

  const seed = getDailySeed();

  // 口语：按 Part 比例随机
  if (type === 'speaking') {
    const ratio = srsPrefs?.speakingPartRatio ?? DEFAULT_SPEAKING_PART_RATIO;
    return randomizeSpeakingTopics(cards, ratio, seed);
  }

  // 听力：简单主题随机
  if (type === 'listening') {
    return randomizeSimpleTopics(cards, seed, '-listening');
  }

  // 词汇：简单主题随机
  if (type === 'vocab') {
    return randomizeSimpleTopics(cards, seed, '-vocab');
  }

  // 写作：根据用户选择的模式过滤并排序
  if (type === 'writing') {
    const aiWritingCards = cards.filter(c => c.mode === 'translation' || c.mode === 'argument');
    const staticCards = cards.filter(c => c.mode !== 'translation' && c.mode !== 'argument');

    // Cloudflare 私有版：根据用户选择的模式过滤
    if (!isPublicDeploy) {
      const writingMode = options?.writingModeOverride ?? getWritingMode();
      if (writingMode === 'ai') {
        // AI 模式：只返回 AI 卡片
        return aiWritingCards.length > 0 ? randomizeAIWritingTopics(aiWritingCards, seed) : [];
      } else {
        // 静态模式：只返回静态卡片
        return staticCards.length > 0 ? sortStaticWritingCards(staticCards) : [];
      }
    }

    // 公开版：按原有逻辑，AI 优先
    const sortedAI = aiWritingCards.length > 0 ? randomizeAIWritingTopics(aiWritingCards, seed) : [];
    const sortedStatic = staticCards.length > 0 ? sortStaticWritingCards(staticCards) : [];

    return [...sortedAI, ...sortedStatic];
  }

  // 默认：不排序
  return cards;
};

export function buildTodayPlan(
  dueByType: Partial<Record<CardType, CardRecord[]>> = {},
  limits?: DailyLimits,
  srsPrefs?: SrsPreferences,
  options?: TodayPlanOptions
): TodayPlan {
  const effectiveLimits = mergeLimits(limits);

  const skills: TodaySkillPlan[] = CARD_TYPE_ORDER.map((type) => {
    const sorted = sortCardsForPlanning(dueByType[type] ?? [], type, srsPrefs, options);
    const limit = clampLimit(effectiveLimits[type]);
    const plannedCards = limit === 0 ? [] : sorted.slice(0, limit);
    const plannedCount = plannedCards.length;
    const dueCount = sorted.length;
    const overflowCount = Math.max(dueCount - plannedCount, 0);

    return {
      type,
      limit,
      dueCount,
      plannedCount,
      overflowCount,
      plannedCards,
    };
  });

  const totals = skills.reduce(
    (acc, skill) => {
      acc.dueCount += skill.dueCount;
      acc.plannedCount += skill.plannedCount;
      acc.overflowCount += skill.overflowCount;
      return acc;
    },
    { dueCount: 0, plannedCount: 0, overflowCount: 0 }
  );

  return { skills, totals };
}

export async function getTodayPlan(
  db: MyIELTSDatabase = defaultDb,
  now: number = Date.now(),
  options?: TodayPlanOptions
): Promise<TodayPlanResult> {
  const cutoff = endOfDay(now);

  const [settings, speaking, writing, listeningRaw, vocab] = await Promise.all([
    getSettings('app', db),
    getDueCards({ type: 'speaking', onOrBefore: cutoff }, db),
    getDueCards({ type: 'writing', onOrBefore: cutoff }, db),
    getDueCards({ type: 'listening', onOrBefore: cutoff }, db),
    getDueCards({ type: 'vocab', onOrBefore: cutoff }, db),
  ]);

  // 听力卡片去重：如果有 cloze 卡片到期，排除对应的源卡片
  // 避免同一个音频片段出现两张卡片（整句听写 + 填空）
  const clozeSourceIds = new Set(
    listeningRaw
      .filter(c => c.id.endsWith('-listening-cloze') && c.sourceId)
      .map(c => c.sourceId!)
  );
  const listening = listeningRaw.filter(c => !clozeSourceIds.has(c.id));

  const rawDue: Partial<Record<CardType, CardRecord[]>> = {
    speaking,
    writing,
    listening,
    vocab,
  };

  const limits = mergeLimits(settings?.dailyLimits);
  const plan = buildTodayPlan(rawDue, limits, settings?.srsPreferences, options);

  return { plan, limits, rawDue, cutoff };
}

export function snapshotFromPlan(plan: TodayPlan, generatedAt: number, note?: string): TodaySnapshot {
  return {
    id: TODAY_SNAPSHOT_ID,
    generatedAt,
    totals: plan.totals,
    skills: plan.skills.map((skill) => ({
      type: skill.type,
      dueCount: skill.dueCount,
      plannedCount: skill.plannedCount,
      overflowCount: skill.overflowCount,
      limit: skill.limit,
    })),
    note,
  };
}
